import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import {
  CallDurationsSummary,
  CallNoteCallee,
  CallNoteCaller,
  CallNoteDetails,
} from './call-details.model';
import { callStatusLabel, callStatusTone } from '../call-information.model';
import { EmployeeApiService } from '../../../core/services/employee-api.service';
import { Employee } from '../../../core/services/employee.model';
import { HlmAlertComponent, HlmBadgeComponent, HlmBtnDirective, HlmIconComponent, HlmTooltipDirective, ToastService } from '../../../ui';
import { formatDuration } from '../../../shared/utils/format-duration';

/** Signature of the fetcher passed by the parent dialog. */
export type CallNoteDetailsFetchFn = () => Observable<CallNoteDetails>;

const BERLIN_FMT = new Intl.DateTimeFormat('de-DE', {
  timeZone: 'Europe/Berlin',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
});

function formatBerlin(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return BERLIN_FMT.format(d).replace(', ', ' ');
}

function joinName(caller: CallNoteCaller | null): string {
  if (!caller) return '—';
  const parts = [caller.salutation, caller.firstname, caller.lastname].filter(Boolean);
  return parts.length ? parts.join(' ') : '—';
}

function formatCallee(callee: CallNoteCallee | null): string {
  if (!callee) return '—';
  const parts = [callee.vorwahl, callee.prefix, callee.suffix].filter(Boolean);
  return parts.length ? parts.join(' ') : '—';
}

/**
 * Translation maps for raw API status / type / sub-type values.
 * Per CLAUDE.md §10 we never display raw enums to operators — wrap each via the
 * label() helper so unknown values fall through to the original string.
 */
const TYPE_LABELS: Record<string, string> = {
  GESPRAECH: 'Conversation',
  ANRUF: 'Call',
  TESTAUFRUF: 'Test call',
  TESTANRUF: 'Test call',
  RUECKRUF: 'Callback',
  WEITERLEITUNG: 'Forwarded',
};

const STATUS_LABELS: Record<string, string> = {
  UNGELESEN: 'Unread',
  GELESEN: 'Read',
  NEU: 'New',
  GEPRUEFT: 'Reviewed',
  GESPERRT: 'Locked',
  ABGESCHLOSSEN: 'Closed',
};

const COLOR_LABELS: Record<string, string> = {
  NONE: '—',
  RED: 'Red',
  GREEN: 'Green',
  YELLOW: 'Yellow',
  BLUE: 'Blue',
};

const FOLDER_TYPE_LABELS: Record<string, string> = {
  INBOX: 'Inbox',
  OUTBOX: 'Outbox',
  SENT: 'Sent',
  DRAFT: 'Draft',
  TRASH: 'Trash',
};

function label(value: string | null | undefined, dict: Record<string, string>): string {
  if (!value) return '—';
  return dict[value] ?? value;
}

/**
 * Read-only detail view for a single callnote. Shows the full DTO returned by
 * GET /phoneservice/v3/phoneservices/{phoneServiceId}/callnotes/{callNoteId},
 * organised as Hero strip + Caller + Called/Routing + Content + Technical details.
 */
@Component({
  selector: 'cmt-call-note-details-tab',
  standalone: true,
  imports: [HlmAlertComponent, HlmBadgeComponent, HlmBtnDirective, HlmIconComponent, HlmTooltipDirective],
  templateUrl: './call-note-details-tab.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'flex min-h-0 flex-1 flex-col' },
})
export class CallNoteDetailsTabComponent implements OnInit {
  /** Fetcher passed by the parent dialog. Does the 2-step resolve + fetch. */
  readonly fetchFn = input.required<CallNoteDetailsFetchFn>();
  /** If set, the tab shows this message instead of fetching. */
  readonly unavailableReason = input<string | null>(null);
  /** Optional duration breakdown shown in Overview (talk / process / ring / wrap-up). */
  readonly durations = input<CallDurationsSummary | null>(null);
  /**
   * EB-3813: raw call status (e.g. `vermittelt`) from the grid row. Used by
   * the Overview card to mark Talk duration as "incl. transfer" when the call
   * was forwarded — without this hint the value (which includes post-transfer
   * caller↔mediated-party talk) looks inconsistent with Process / Bearb.dauer.
   */
  readonly callStatus = input<string | null | undefined>(null);
  /** Billed flag — true when the call has at least one billing entry. null = unknown. */
  readonly isBilled = input<boolean | null>(null);
  /**
   * Customer ID forwarded from the dialog data. Not displayed anywhere; only
   * used as the route root for the "Open phone service / user account /
   * customer" jump buttons. Without it the jump buttons stay hidden.
   */
  readonly customerId = input<string | null | undefined>(null);
  /**
   * Callnote ID for the current call — used by the empty-state message so the
   * operator sees which ID came back empty (helps debugging when the backend
   * returns 200 with no body for certain test calls).
   */
  readonly callNoteId = input<number | null | undefined>(null);
  /**
   * Fallback agent display name — taken from the CallInformation row in the
   * Calls grid. The callnote response itself can be missing `agentName` /
   * `handledAgentName` even when the grid row has it (different upstream
   * sources). When that happens, surface this value in the Agent row instead
   * of an em-dash.
   */
  readonly fallbackAgentName = input<string | null | undefined>(null);

  private readonly destroyRef = inject(DestroyRef);
  private readonly toast = inject(ToastService);
  private readonly router = inject(Router);
  private readonly employeeApi = inject(EmployeeApiService);
  /**
   * The parent CDK Dialog ref — present whenever this tab is rendered inside
   * the Call Details dialog (which it always is). Optional injection keeps the
   * component testable outside a dialog harness. The jump handlers close the
   * dialog so the user lands on a clean detail page instead of underneath the
   * stacked modal.
   */
  private readonly dialogRef = inject(MatDialogRef, { optional: true });

  /** True when we have a customerId — jump buttons stay hidden when false. */
  readonly canJump = computed(() => {
    const id = this.customerId();
    return id != null && String(id).length > 0;
  });

  /** Close the dialog (if any) and navigate to the customer detail page. */
  openCustomer(): void {
    const cid = this.customerId();
    if (!cid) return;
    this.dialogRef?.close();
    void this.router.navigate(['/', cid]);
  }

  /** Close the dialog and navigate to the Phone Service settings page. */
  openPhoneService(phoneServiceId: number | null | undefined): void {
    const cid = this.customerId();
    if (!cid || phoneServiceId == null) return;
    this.dialogRef?.close();
    void this.router.navigate(['/', cid, 'ps', phoneServiceId, 'settings']);
  }

  /** Close the dialog and navigate to the User Account details page. */
  openUserAccount(userAccountId: number | null | undefined): void {
    const cid = this.customerId();
    if (!cid || userAccountId == null) return;
    this.dialogRef?.close();
    void this.router.navigate(['/', cid, 'ua', userAccountId, 'account-details']);
  }

  readonly note = signal<CallNoteDetails | null>(null);
  readonly loading = signal(false);
  readonly error = signal<{ status: number; message: string; correlationId?: string } | null>(null);

  /**
   * Enriched employee record for the handling agent, populated by an effect
   * that fires whenever the loaded callnote changes. Drives the multi-line
   * Agent row in Overview (name + login on line 1, location · team on line 2).
   * `null` until lookup completes or when the agent can't be resolved.
   */
  readonly agentEmployee = signal<Employee | null>(null);

  /**
   * Effective agent name shown when the enriched lookup hasn't resolved (yet
   * or at all). Falls through: callnote agentName → CallInformation row
   * fallback → em-dash.
   */
  readonly agentBaseName = computed(() => {
    return this.note()?.agentName?.trim()
      || this.fallbackAgentName()?.trim()
      || '';
  });

  /** "Office Berlin · Technik" when both present, just one of them otherwise. */
  readonly agentMetaLine = computed(() => {
    const e = this.agentEmployee();
    if (!e) return '';
    return [e.location, e.team].filter((s): s is string => !!s).join(' · ');
  });

  /** Login formatted as "(login)" for the inline accent next to the name. */
  readonly agentLoginAccent = computed(() => {
    const login = this.agentEmployee()?.login;
    return login ? `(${login})` : '';
  });

  /** EB-3813 follow-up: agent's employee ID rendered as "#171572" mono chip. */
  readonly agentIdAccent = computed(() => {
    const id = this.agentEmployee()?.id;
    return id != null ? `#${id}` : '';
  });

  readonly callerName = computed(() => joinName(this.note()?.caller ?? null));
  readonly calleeFormatted = computed(() => formatCallee(this.note()?.callee ?? null));

  /** True when at least one caller field has data — drives whether to render the empty hint. */
  readonly hasCallerInfo = computed(() => {
    const n = this.note();
    if (!n) return false;
    const c = n.caller;
    return Boolean(
      n.clipTelephone ||
      c?.firstname || c?.lastname || c?.salutation ||
      c?.company || c?.phone || c?.email,
    );
  });

  /** Translated type label (e.g. GESPRAECH → "Conversation"). */
  readonly typeLabel = computed(() => label(this.note()?.type, TYPE_LABELS));
  /** Translated sub-type label — delegates to the shared `callStatusLabel`
   *  helper so the Calls grid and this dialog speak the same vocabulary
   *  (vermittelt → "Forwarded", Notiz → "Note", vbOrphan → "Voicebot abandoned", …). */
  readonly subTypeLabel = computed(() => callStatusLabel(this.note()?.callNoteType));
  /** Raw sub-type value (kept for the "Translation (RAW)" chip + tone selection). */
  readonly subTypeRaw = computed(() => this.note()?.callNoteType ?? '');
  /** Tone classes for the sub-type chip — shared with the calls grid statusCellRenderer. */
  readonly subTypeTone = computed(() => callStatusTone(this.subTypeRaw()));
  /** Translated read-state label (e.g. UNGELESEN → "Unread"). */
  readonly statusLabel = computed(() => label(this.note()?.status, STATUS_LABELS));
  /** Translated color label (NONE → em-dash). */
  readonly colorLabel = computed(() => label(this.note()?.color, COLOR_LABELS));
  /** Translated folder-type label (INBOX → "Inbox"). */
  folderTypeLabel(folderType: string | null | undefined): string {
    return label(folderType, FOLDER_TYPE_LABELS);
  }

  /**
   * De-duplicate content fields: when shortContent is a strict prefix of (or equal to)
   * content, only the longer `content` is shown. Otherwise both are kept (lead + body).
   */
  readonly contentForDisplay = computed<{ short: string | null; full: string | null }>(() => {
    const n = this.note();
    if (!n) return { short: null, full: null };
    const short = (n.shortContent ?? '').trim();
    const full = (n.content ?? '').trim();
    if (!short && !full) return { short: null, full: null };
    if (short && full) {
      // identical or full starts with short → only full
      if (full === short || full.startsWith(short)) return { short: null, full };
      return { short, full };
    }
    return { short: short || null, full: full || null };
  });

  constructor() {
    // Enrich the Agent row: when the callnote arrives, try to resolve the
    // agent's employee record. Two paths:
    //   1. note.agentId present (doc-api/mitarbeiter) → direct GET by id.
    //   2. Only agentName known (rich phoneservice path) → searchEmployees
    //      with the trimmed name and accept an EXACT "First Last" match.
    //      Ambiguous matches resolve to null so the UI stays honest.
    // Either way the result feeds agentEmployee — falsy values cleanly fall
    // back to the agentBaseName + em-dash in the template.
    effect(() => {
      const n = this.note();
      const id = n?.agentId ?? null;
      const name = (n?.agentName ?? this.fallbackAgentName() ?? '').trim();
      this.agentEmployee.set(null);
      if (id != null) {
        this.employeeApi.findEmployeeById(id)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((e) => this.agentEmployee.set(e ?? null));
        return;
      }
      if (name.length >= 3) {
        this.employeeApi.searchEmployees(name)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((list) => {
            const exact = list.find((e) =>
              `${e.firstName ?? ''} ${e.lastName ?? ''}`.trim() === name,
            );
            this.agentEmployee.set(exact ?? null);
          });
      }
    });
  }

  ngOnInit(): void {
    if (!this.unavailableReason()) this.reload();
  }

  reload(): void {
    this.loading.set(true);
    this.error.set(null);
    this.fetchFn()()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => {
          this.note.set(data);
          this.loading.set(false);
        },
        error: (err: HttpErrorResponse) => {
          this.note.set(null);
          this.error.set({
            status: err.status ?? 0,
            message: typeof err.error === 'string' ? err.error : err.message ?? 'Failed to load call details',
            correlationId: err.headers?.get('correlation-id') ?? undefined,
          });
          this.loading.set(false);
        },
      });
  }

  formatDate(iso: string | null | undefined): string {
    return formatBerlin(iso);
  }

  /** Format a seconds duration into mm:ss (or em-dash when null/0). */
  formatSeconds(seconds: number | null | undefined): string {
    if (seconds == null) return '—';
    return formatDuration(seconds);
  }

  /** EB-3813: true when the call was forwarded — drives the Talk "incl. transfer" hint. */
  readonly isTransferred = computed(() => (this.callStatus() ?? '') === 'vermittelt');

  /** True when at least one of the duration fields carries a non-null value. */
  readonly hasDurations = computed(() => {
    const d = this.durations();
    if (!d) return false;
    return (
      d.callDurationInSeconds != null ||
      d.processDurationInSeconds != null ||
      d.customerConnectSeconds != null ||
      d.agentConnectSeconds != null ||
      d.postProcessDurationInSeconds != null
    );
  });

  /** True when the color tag is meaningful (set + not the sentinel "NONE"). */
  readonly hasColor = computed(() => {
    const c = this.note()?.color;
    return !!c && c !== 'NONE';
  });

  /**
   * Copy a value to the clipboard with a brief toast confirmation.
   * Used for Callnote ID, User Account ID, Phone Service ID — operators paste these
   * into Graylog / support tickets, so the affordance lives next to each ID.
   */
  copyValue(value: string | number | null | undefined, label: string): void {
    if (value == null || value === '') return;
    void navigator.clipboard.writeText(String(value)).then(
      () => this.toast.open(`${label} copied`, '', { duration: 1500 }),
      () => this.toast.open(`Could not copy ${label}`, '', { duration: 2000, tone: 'error' }),
    );
  }
}
